/**
 * Boards home filtering: which boards the home grid shows for a text query and
 * a scope (All / Personal / one team). Pure — boards and a teamId→team Map in,
 * plain values out — so BoardsHome only renders and the rules are testable
 * without mounting anything. The in-board task filters are boardFilters.js.
 *
 * A board's scope is `personal` unless it is a team board (`visibility ===
 * 'team'`), whatever its location: cloud and on-this-device boards are both
 * the user's own. Team boards are keyed `team:<teamId>`.
 */

export const SCOPE_ALL = 'all';
export const SCOPE_PERSONAL = 'personal';

const isTeamBoard = (board) => board?.visibility === 'team';

export function teamScopeKey(teamId) {
  return `team:${String(teamId)}`;
}

export function boardScopeKey(board) {
  return isTeamBoard(board) ? teamScopeKey(board.teamId) : SCOPE_PERSONAL;
}

/**
 * The team name a tile shows for a team board, or null for a personal one.
 * Same lookup and the same 'Team' fallback as BoardTile's badge, so a chip,
 * a tile and a text match never disagree about what a team is called.
 */
export function boardTeamLabel(board, teamById) {
  if (!isTeamBoard(board)) return null;
  return teamById?.get(String(board.teamId))?.name || 'Team';
}

/**
 * The chip row: All, Personal, then one chip per team that has at least one
 * board here (by name), each with its board count. Counts are over ALL
 * boards, not the text-filtered ones, so a chip's number does not jump while
 * typing. Empty when there are no team boards — with nothing to split, the
 * row would only ever offer "All" and "Personal" showing the same boards.
 */
export function scopeChips(boards, teamById) {
  const list = boards || [];
  const teams = new Map();
  let personal = 0;
  for (const b of list) {
    if (!isTeamBoard(b)) {
      personal += 1;
      continue;
    }
    const key = teamScopeKey(b.teamId);
    const entry = teams.get(key) || { key, label: boardTeamLabel(b, teamById), count: 0 };
    entry.count += 1;
    teams.set(key, entry);
  }
  if (teams.size === 0) return [];
  const teamChips = [...teams.values()].sort(
    (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }) || a.key.localeCompare(b.key)
  );
  return [
    { key: SCOPE_ALL, label: 'All', count: list.length },
    { key: SCOPE_PERSONAL, label: 'Personal', count: personal },
    ...teamChips,
  ];
}

/**
 * The scope actually applied: the stored one while a chip for it exists,
 * otherwise All — e.g. after leaving the team whose chip was selected, or once
 * the last team board is gone and the row disappears.
 */
export function effectiveScope(scope, chips) {
  if (!scope || scope === SCOPE_ALL) return SCOPE_ALL;
  return (chips || []).some((c) => c.key === scope) ? scope : SCOPE_ALL;
}

/**
 * Text matches the board name or, for a team board, its team's name — so
 * typing a team name finds that team's boards. Intersected with the scope.
 */
export function boardMatchesHome(board, { text = '', scope = SCOPE_ALL } = {}, teamById) {
  if (scope !== SCOPE_ALL && boardScopeKey(board) !== scope) return false;
  const q = String(text || '').trim().toLowerCase();
  if (!q) return true;
  if ((board.name || '').toLowerCase().includes(q)) return true;
  const team = boardTeamLabel(board, teamById);
  return !!team && team.toLowerCase().includes(q);
}
